import { useEffect, useId, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Gauge, PauseCircle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  itsmApi,
  type ItsmCollection, type ItsmItem, type ItsmLevel, type ItsmPriority, type ItsmSlaClock,
} from '../../services/api';
import {
  LEVELS, PRIORITIES, clockState, computePriority, overrideDiffers, relTime, ticketSlaState,
  useSlaPolicy, type ClockState, type RaisePriorityValue,
} from './itsmSla';

/**
 * ITIL priority from impact x urgency, and the SLA clocks (EP-27 FE-6, PO46-1308).
 *
 * The matrix lives on the server as config of record (`/api/itsm-sla/policy`) and
 * the server computes the stored priority. It is fetched here only to PREVIEW
 * it, so the Raise form can say what a choice will mean before it is sent. A
 * priority that differs from the matrix is an override and needs a reason; the
 * server refuses one without.
 *
 * Built to the HS design standard (v1.1): slate ramp and status tokens only (no
 * gray-*, no raw hex), nothing below 11px, status always carries a word or an
 * icon as well as a colour, labels bound to their fields with help and error
 * text announced through aria-describedby, and a visible focus ring.
 */

// Status tokens: danger red-600/400, warning amber-700/400, success green-700/400.
const DANGER_BADGE = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
const WARNING_BADGE = 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';

const PRIORITY_STYLE: Record<string, string> = {
  critical: DANGER_BADGE,
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium: WARNING_BADGE,
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const muted = 'text-slate-600 dark:text-slate-400';
const labelCls = 'block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1';
const fieldCls = 'w-full px-2 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60';
const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-900';

/** Small breached / due-soon badge for a board row: a word and an icon, not
 *  just a colour. */
export function SlaBadge({ item, warnAt }: { item: ItsmItem; warnAt: number }) {
  const state = ticketSlaState(item, warnAt);
  if (!state) return null;
  const breached = state === 'breached';
  return (
    <span
      title={breached ? 'An SLA target has been breached' : 'An SLA target is due soon'}
      className={clsx('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium uppercase',
        breached ? DANGER_BADGE : WARNING_BADGE)}
    >
      {breached
        ? <AlertTriangle size={11} aria-hidden="true" />
        : <Clock size={11} aria-hidden="true" />}
      {breached ? 'Breached' : 'Due soon'}
    </span>
  );
}

export function PriorityPill({ priority }: { priority?: string }) {
  if (!priority) return null;
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-[11px] font-medium uppercase',
      PRIORITY_STYLE[priority] || PRIORITY_STYLE.low)}>
      <span className="sr-only">Priority </span>{priority}
    </span>
  );
}

/**
 * Impact / urgency / priority inputs for the Raise form. Controlled: the
 * parent holds the value and sends what `raisePriorityPayload` returns.
 */
export function RaisePriorityFields({ value, onChange, showErrors }: {
  value: RaisePriorityValue;
  onChange: (patch: Partial<RaisePriorityValue>) => void;
  /** Set after a refused submit, so a missing reason is announced. */
  showErrors?: boolean;
}) {
  const pol = useSlaPolicy();
  const id = useId();
  const computed = computePriority(pol, value.impact, value.urgency);
  const differs = overrideDiffers(pol, value);
  const reasonMissing = differs && !value.reason.trim();

  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">Priority</legend>
      <p id={`${id}-help`} className={clsx('text-xs', muted)}>
        Priority comes from impact and urgency. Leave both unassessed and the
        desk triages it.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${id}-impact`} className={labelCls}>Impact</label>
          <select id={`${id}-impact`} className={fieldCls} value={value.impact}
            aria-describedby={`${id}-help`}
            title="How much of the service or how many people it affects"
            onChange={(e) => onChange({ impact: e.target.value })}>
            <option value="">Not assessed</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`${id}-urgency`} className={labelCls}>Urgency</label>
          <select id={`${id}-urgency`} className={fieldCls} value={value.urgency}
            aria-describedby={`${id}-help`}
            title="How soon it has to be fixed before the harm grows"
            onChange={(e) => onChange({ urgency: e.target.value })}>
            <option value="">Not assessed</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300"
        aria-live="polite">
        <Gauge size={12} className="text-slate-400" aria-hidden="true" />
        Priority
        {computed ? (
          <>
            <PriorityPill priority={computed} />
            <span className={muted}>from the matrix</span>
          </>
        ) : (
          <span className={muted}>
            {pol ? 'set impact or urgency to compute it' : 'Loading…'}
          </span>
        )}
        <label className="ml-auto flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={value.override} className={focusRing}
            onChange={(e) => onChange({
              override: e.target.checked,
              priority: e.target.checked ? (value.priority || computed || 'medium') : '',
              reason: e.target.checked ? value.reason : '',
            })} />
          Override
        </label>
      </div>

      {value.override && (
        <div className="space-y-2">
          <div>
            <label htmlFor={`${id}-priority`} className={labelCls}>Priority</label>
            <select id={`${id}-priority`} className={fieldCls} value={value.priority}
              onChange={(e) => onChange({ priority: e.target.value })}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}{p === computed ? ' (matrix)' : ''}</option>
              ))}
            </select>
          </div>
          {differs && (
            <div>
              <label htmlFor={`${id}-reason`} className={labelCls}>
                Reason for the override
              </label>
              <input id={`${id}-reason`} className={fieldCls} value={value.reason}
                required aria-invalid={showErrors && reasonMissing ? true : undefined}
                aria-describedby={`${id}-reason-help${showErrors && reasonMissing ? ` ${id}-reason-err` : ''}`}
                onChange={(e) => onChange({ reason: e.target.value })} />
              <p id={`${id}-reason-help`} className={clsx('mt-1 text-xs', muted)}>
                {value.priority} differs from the matrix ({computed}). The reason
                is recorded on the ticket with your name.
              </p>
              {showErrors && reasonMissing && (
                <p id={`${id}-reason-err`} role="alert"
                  className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Give a reason, or set the priority back to {computed}.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}

const CLOCK_STYLE: Record<ClockState, string> = {
  met: 'text-green-700 dark:text-green-400',
  'met-late': 'text-red-600 dark:text-red-400',
  breached: 'text-red-600 dark:text-red-400',
  paused: muted,
  warning: 'text-amber-700 dark:text-amber-400',
  within: 'text-slate-800 dark:text-slate-200',
};

function ClockRow({ label, clock, pausedSince, warnAt, now }: {
  label: string; clock?: ItsmSlaClock; pausedSince?: string; warnAt: number; now: number;
}) {
  const state = clockState(clock, pausedSince, warnAt, now);
  if (!clock || !state) return null;
  let text: string;
  if (state === 'met' || state === 'met-late') {
    text = `Met ${relTime(clock.metAt, now)}${state === 'met-late' ? ', late' : ''}`;
  } else if (state === 'paused') {
    text = 'Paused; the due time moves out by the time on hold';
  } else if (state === 'breached') {
    text = `Was due ${relTime(clock.dueAt, now)}`;
  } else {
    text = `Due ${relTime(clock.dueAt, now)}${state === 'warning' ? ' (due soon)' : ''}`;
  }
  const late = state === 'breached' || state === 'met-late';
  return (
    <div className="flex items-center gap-2 text-xs">
      <dt className={clsx('w-20 shrink-0', muted)}>{label}</dt>
      <dd className={clsx('flex flex-1 items-center gap-1 min-w-0', CLOCK_STYLE[state])}>
        {state === 'met' && <CheckCircle2 size={12} aria-hidden="true" />}
        {state === 'paused' && <PauseCircle size={12} aria-hidden="true" />}
        {late && <AlertTriangle size={12} aria-hidden="true" />}
        {(state === 'warning' || state === 'within') && <Clock size={12} aria-hidden="true" />}
        <time dateTime={clock.dueAt} title={new Date(clock.dueAt).toLocaleString()}>{text}</time>
        {late && (
          <span className={clsx('ml-auto px-1.5 py-0.5 rounded text-[11px] font-medium uppercase', DANGER_BADGE)}>
            Breached
          </span>
        )}
      </dd>
    </div>
  );
}

/**
 * The ticket panel's priority section: impact and urgency editable (the server
 * recomputes the priority), an override that needs a reason, and the clocks.
 */
export function PrioritySlaPanel({ collection, item, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  onChanged: (updated?: ItsmItem) => void;
}) {
  const pol = useSlaPolicy();
  const id = useId();
  const warnAt = pol?.warnAt || 0.75;
  const [saving, setSaving] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [draftPriority, setDraftPriority] = useState<string>(item.priority || 'medium');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Relative times stay true without a refresh.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    setOverriding(false);
    setReason('');
    setTried(false);
    setDraftPriority(item.priority || 'medium');
  }, [item._id, item.priority]);

  const basis = item.priorityBasis || {};
  // The ticket's own stamped matrix answer first: a later policy change does
  // not re-price an old ticket.
  const computed = basis.computed
    || computePriority(pol, item.impact || '', item.urgency || '');
  const differs = !!computed && draftPriority !== computed;
  const reasonMissing = differs && !reason.trim();

  const save = async (what: string, patch: Partial<ItsmItem>, ok: string) => {
    setSaving(what);
    try {
      const updated = await itsmApi.update(collection, item._id, patch);
      toast.success(ok);
      onChanged(updated);
    } catch (err: unknown) {
      // The server says which matrix value it expected and what to send.
      toast.error((err instanceof Error && err.message) || 'Could not save the priority. Try again.', { duration: 8000 });
    } finally {
      setSaving('');
    }
  };

  const saveOverride = () => {
    setTried(true);
    if (reasonMissing) return;
    if (draftPriority === item.priority) { setOverriding(false); return; }
    save('priority', {
      priority: draftPriority as ItsmPriority,
      ...(reason.trim() ? { priorityReason: reason.trim() } : {}),
    }, differs ? 'Priority overridden' : 'Priority set from the matrix');
  };

  const sla = item.sla;

  return (
    <section aria-labelledby={`${id}-h`}>
      <h3 id={`${id}-h`}
        className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-1.5">
        <Gauge size={12} aria-hidden="true" /> Priority
        {basis.policyVersion && (
          <span className={clsx('ml-auto text-[11px] font-normal', muted)}>
            matrix v{basis.policyVersion}
          </span>
        )}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${id}-impact`} className={labelCls}>Impact</label>
          <select id={`${id}-impact`} className={fieldCls} value={item.impact || ''}
            disabled={!!saving} aria-describedby={`${id}-ia-help`}
            onChange={(e) => save('impact', { impact: e.target.value as ItsmLevel },
              'Impact updated; priority recomputed')}>
            {!item.impact && <option value="">Not assessed</option>}
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`${id}-urgency`} className={labelCls}>Urgency</label>
          <select id={`${id}-urgency`} className={fieldCls} value={item.urgency || ''}
            disabled={!!saving} aria-describedby={`${id}-ia-help`}
            onChange={(e) => save('urgency', { urgency: e.target.value as ItsmLevel },
              'Urgency updated; priority recomputed')}>
            {!item.urgency && <option value="">Not assessed</option>}
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>
      <p id={`${id}-ia-help`} className={clsx('mt-1 text-xs', muted)}>
        Changing either re-assesses the ticket: the priority goes back to the
        matrix, replacing any override.
      </p>

      <div className="mt-2 flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300"
        aria-live="polite">
        <PriorityPill priority={item.priority} />
        {saving && <span className={muted}>Saving…</span>}
        {!saving && basis.source === 'override' && (
          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
            <AlertTriangle size={12} aria-hidden="true" />
            Overridden (matrix says {computed || 'nothing'})
          </span>
        )}
        {!saving && basis.source === 'matrix' && <span className={muted}>from the matrix</span>}
        {!saving && basis.source === 'manual' && <span className={muted}>set by hand</span>}
        {!overriding && (
          <button type="button" onClick={() => setOverriding(true)} disabled={!!saving}
            className={clsx('ml-auto px-2 py-1 rounded-lg text-xs text-accent hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50', focusRing)}>
            Override
          </button>
        )}
      </div>
      {basis.source === 'override' && basis.reason && (
        <p className={clsx('mt-1 text-xs', muted)}>
          Reason: “{basis.reason}”{basis.by && <> ({basis.by}</>}
          {basis.at && <>, {relTime(basis.at, now)}</>}{basis.by && ')'}
        </p>
      )}

      {overriding && (
        <div className="mt-2 space-y-2 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
          <div>
            <label htmlFor={`${id}-priority`} className={labelCls}>Priority</label>
            <select id={`${id}-priority`} className={fieldCls} value={draftPriority}
              onChange={(e) => setDraftPriority(e.target.value)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}{p === computed ? ' (matrix)' : ''}</option>
              ))}
            </select>
          </div>
          {differs && (
            <div>
              <label htmlFor={`${id}-reason`} className={labelCls}>Reason for the override</label>
              <input id={`${id}-reason`} className={fieldCls} value={reason} required
                aria-invalid={tried && reasonMissing ? true : undefined}
                aria-describedby={`${id}-reason-help${tried && reasonMissing ? ` ${id}-reason-err` : ''}`}
                onChange={(e) => setReason(e.target.value)} />
              <p id={`${id}-reason-help`} className={clsx('mt-1 text-xs', muted)}>
                Recorded on the ticket and in the activity log with your name.
              </p>
              {tried && reasonMissing && (
                <p id={`${id}-reason-err`} role="alert"
                  className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Give a reason, or choose {computed} to follow the matrix.
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button"
              onClick={() => { setOverriding(false); setTried(false); setDraftPriority(item.priority || 'medium'); }}
              className={clsx('px-2.5 py-1 text-xs rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800', focusRing)}>
              Cancel
            </button>
            <button type="button" onClick={saveOverride} disabled={!!saving}
              className={clsx('px-2.5 py-1 text-xs rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-50', focusRing)}>
              {saving === 'priority' ? 'Saving…' : 'Save priority'}
            </button>
          </div>
        </div>
      )}

      {sla && (sla.response || sla.resolution) && (
        <div className="mt-3">
          <h4 className={clsx('text-xs font-medium mb-1.5 flex items-center gap-1', muted)}>
            <Clock size={12} aria-hidden="true" /> SLA
            {sla.pausedSince && (
              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium uppercase bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <PauseCircle size={11} aria-hidden="true" />
                Paused <span className="normal-case font-normal">{relTime(sla.pausedSince, now)}</span>
              </span>
            )}
          </h4>
          <dl className="space-y-1.5">
            <ClockRow label="Response" clock={sla.response} pausedSince={sla.pausedSince}
              warnAt={warnAt} now={now} />
            <ClockRow label="Resolution" clock={sla.resolution} pausedSince={sla.pausedSince}
              warnAt={warnAt} now={now} />
          </dl>
        </div>
      )}
    </section>
  );
}
